import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Youtube, MapPin, Phone, Mail, HeartPulse } from "lucide-react";
import { useSettings, assetUrl } from "@/context/SettingsContext";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/courses", label: "Courses" },
  { to: "/facilities", label: "Facilities" },
  { to: "/placements", label: "Clinical Training" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  const { settings } = useSettings();
  const brand = settings?.branding || {};
  const contact = settings?.contact || {};
  const logoLight = brand.logoLight && brand.showLogoImage !== false ? assetUrl(brand.logoLight) : "";
  const shortName = brand.shortName || "Ambigara";
  const subtitle = brand.navSubtitle || "College of Nursing";
  const tagline = brand.tagline || "";

  const socials = [
    { key: "instagram", url: contact.instagram, Icon: Instagram },
    { key: "linkedin", url: contact.linkedin, Icon: Linkedin },
    { key: "facebook", url: contact.facebook, Icon: Facebook },
    { key: "youtube", url: contact.youtube, Icon: Youtube },
  ].filter((s) => s.url);

  return (
    <footer data-testid="site-footer" style={{ backgroundColor: "var(--brand-dark)" }} className="text-slate-200 relative">
      <div className="absolute inset-0 bg-grain-dark opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              {logoLight ? (
                <img src={logoLight} alt={shortName} className="h-14 w-auto object-contain" />
              ) : (
                <span className="w-12 h-12 rounded-xl bg-white grid place-items-center shadow-lg" style={{ color: "var(--brand-primary)" }}>
                  <HeartPulse className="w-6 h-6" />
                </span>
              )}
              <span className="leading-tight">
                <span className="block font-heading text-2xl text-white font-semibold">{shortName}</span>
                <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-300 font-medium">{subtitle}</span>
              </span>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed max-w-md">{tagline}</p>
            {socials.length > 0 && (
              <div className="flex items-center gap-3 mt-6">
                {socials.map(({ key, url, Icon }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${key}`}
                    className="w-10 h-10 rounded-full border border-white/20 grid place-items-center hover:bg-white hover:brand-primary-text transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <h4 className="font-heading text-white text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-slate-300 hover:text-white text-sm transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-heading text-white text-lg mb-4">Get in Touch</h4>
            <ul className="space-y-3.5 text-sm">
              {contact.address && (
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 brand-secondary-text shrink-0" />
                  <span className="text-slate-300 leading-relaxed">{contact.address}</span>
                </li>
              )}
              {contact.officePhone && (
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 brand-secondary-text shrink-0" />
                  <a
                    href={`tel:${(contact.officePhone || "").replace(/\s/g, "")}`}
                    data-testid="footer-phone"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {contact.officePhone}
                  </a>
                </li>
              )}
              {contact.email && (
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 brand-secondary-text shrink-0" />
                  <a
                    href={`mailto:${contact.email}`}
                    data-testid="footer-email"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {contact.email}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-6 flex items-center justify-center gap-3">
          <p className="text-slate-400 text-xs">
            © {new Date().getFullYear()} {brand.collegeName || "Ambigara Chowdaiah College of Nursing"}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
